/**
 * API Endpoint Constants
 *
 * This file lists every URL the frontend uses to talk to the backend.
 * Grouped by feature area so it's easy to find what you're looking for.
 * Functions like `BY_ID(id)` build a URL for a specific record, e.g. /users/42.
 */

export const API_ENDPOINTS = {

  // ── Who you are ──────────────────────────────────────────────────────────
  // Handles logging in, logging out, and verifying identity tokens.
  AUTH: {
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',    // Renew a session without re-entering credentials
    VERIFY: '/auth/verify',
  },

  // ── Platform users (brokers, admins, staff) ───────────────────────────────
  // Create accounts, look up profiles, and manage login sessions.
  USERS: {
    BASE: '/users',
    REGISTER: '/users/register',
    LOGOUT: '/users/logout',
    BY_ID: (userId) => `/users/${userId}`,
    BY_USER_ID: (userId) => `/users/by-user-id/${userId}`,
    PROFILE: '/users/profile',
    // Admin action — PATCH { reset: true } to clear a user's TOTP factors.
    // Used by the Team Members / Organizations drawers so support / admin
    // can bail a locked-out user out of the MFA gate. Gated in the UI to
    // PROD + admin / super-admin viewers.
    SECURITY_MFA: (userId) => `/users/${userId}/security/mfa`,
  },

  // ── Operators (charter companies) ─────────────────────────────────────────
  // The companies that own and operate the aircraft.
  OPERATORS: {
    BASE: '/operators',
    BY_ID: (operatorId) => `/operators/${operatorId}`,
    SEARCH: '/operators/search',
    TRUST_SCORES: (operatorId) => `/operators/${operatorId}/trust-scores`,
  },

  // ── Aircraft ──────────────────────────────────────────────────────────────
  // Details about individual aircraft: specs, onboard amenities, and photos.
  AIRCRAFT: {
    BASE: '/aircrafts',
    BY_ID: (aircraftId) => `/aircrafts/${aircraftId}`,
    AMENITIES: (aircraftId) => `/aircrafts/${aircraftId}/amenities`,
    FEATURES: (aircraftId) => `/aircrafts/${aircraftId}/features`,
    PHOTOS: (aircraftId) => `/aircrafts/${aircraftId}/photos`,
  },

  // Reference table of make/model rows. Both the manual-aircraft
  // wizard's search field and the create-new-model flow live here.
  AIRCRAFT_MAKE_MODELS: {
    BASE: '/aircraft-make-models',
    BY_ID: (makeModelId) => `/aircraft-make-models/${makeModelId}`,
  },

  // ── Organizations (brokerage firms) ───────────────────────────────────────
  // The companies that brokers belong to.
  ORGANIZATIONS: {
    BASE: '/organizations',
    BY_ID: (orgId) => `/organizations/${orgId}`,
    // Per-org branding override for a platform-catalog aircraft.
    // POST creates or replaces (201/200), DELETE removes.
    AIRCRAFT_CUSTOM_PROFILE: (orgId, aircraftId) =>
      `/organizations/${orgId}/aircrafts/${aircraftId}/custom-profile`,
    // Org-scoped aircraft (manually-created; not in the platform catalog).
    AIRCRAFTS: (orgId) => `/organizations/${orgId}/aircrafts`,
    AIRCRAFT_BY_ID: (orgId, aircraftId) =>
      `/organizations/${orgId}/aircrafts/${aircraftId}`,
    // Platform (TrustJet) fee brackets — one row per dollar-range tier.
    // DELETE against the collection URL wipes ALL brackets (dangerous —
    // must be guarded with a confirm on the UI side).
    PLATFORM_FEE_BRACKETS: (orgId) =>
      `/organizations/${orgId}/platform-fee-brackets`,
    PLATFORM_FEE_BRACKET_BY_ID: (orgId, bracketId) =>
      `/organizations/${orgId}/platform-fee-brackets/${bracketId}`,
  },

  // ── Bids (operator proposals for a trip) ─────────────────────────────────
  // When operators respond to a quote request, their response is a "bid".
  // Each bid can have multiple pricing options.
  BIDS: {
    BASE: '/bids',
    BY_ID: (bidId) => `/bids/${bidId}`,
    BY_NUMBER: (bidNumber) => `/bids/by-number/${bidNumber}`,
    BY_BROKER: (brokerOrgId) => `/brokers/${brokerOrgId}/bids`,
    BY_STATUS: (status) => `/bids/by-status/${status}`,
    SHORTLIST: '/bids/shortlisted',
    OPTIONS: '/bid-options',                                   // All pricing options
    OPTIONS_BY_ID: (bidOptionId) => `/bid-options/${bidOptionId}`,
  },

  // ── Messages (email & SMS) ────────────────────────────────────────────────
  // Sending notifications to clients and brokers, and tracking inbound emails.
  MESSAGES: {
    BASE: '/messages',
    BY_ID: (messageId) => `/messages/${messageId}`,
    UNPROCESSED: '/messages/mails/unprocessed',                // Inbound emails not yet actioned
    MARK_PROCESSED: (messageId) => `/messages/mails/${messageId}/process`,
    SEND: '/messages/mails/send',
    SMS_SEND: '/messages/sms/send',
  },

  // ── Status events (in-app notification feed) ─────────────────────────────
  // Cross-domain event stream: a row lands here whenever a domain object
  // (quote / invoice / payment / flight) transitions status, addressed
  // to a specific user. NotificationDrawer consumes it.
  STATUS_EVENTS: {
    BASE: '/status-events',
    UNREAD_COUNT: '/status-events/unread-count',
    READ_ALL: '/status-events/read-all',
    MARK_READ: (statusEventId) => `/status-events/${statusEventId}/read`,
  },

  // ── Quotes (trip requests) ────────────────────────────────────────────────
  // A quote is a client's request for a private jet trip.
  // Passengers are the people who will be on the flight.
  QUOTES: {
    BASE: '/quotes',
    BY_ID: (quoteId) => `/quotes/${quoteId}`,
    PASSENGERS: (quoteId) => `/quotes/${quoteId}/passengers`,
    PASSENGERS_BATCH: (quoteId) => `/quotes/${quoteId}/passengers/batch`, // Add multiple passengers at once
    SUMMARY: '/quotes/summary', // Per-status + per-payment-status counts for the /quotes filter badges
    // Nested-write endpoints — create/update a quote + its bids + bid_options
    // in one transaction. POST always creates; PATCH updates an existing quote
    // and diffs bid_options against DB (options omitted from the payload are
    // soft-unshortlisted). Response carries the full include tree.
    WITH_BIDS: '/quotes-with-bids',
    WITH_BIDS_BY_ID: (quoteId) => `/quotes-with-bids/${quoteId}`,
  },

  // ── My Trips (client-facing trip history) ─────────────────────────────────
  // What clients see when they log in to view their bookings.
  MY_TRIPS: {
    BASE: '/my-trips',
    BIDS: '/my-trips/bids',
    BY_ID: (quoteId) => `/my-trips/${quoteId}`,
  },

  // ── Clients (the people booking trips) ───────────────────────────────────
  // Client profiles, their saved payment methods, and their passengers.
  CLIENTS: {
    BASE: '/clients',
    BY_ID: (clientId) => `/clients/${clientId}`,
    SUMMARY: '/clients/summary',                               // Total + filtered_count for the Clients list header
    PASSENGERS: (clientId) => `/clients/${clientId}/passengers`,
    PASSENGERS_BATCH: (clientId) => `/clients/${clientId}/passengers/batch`,
    CLIENT_SECRETS: '/clients/client-secrets',                 // Generate a secure token to initialise the payment widget
    PRIORITY_VAULT: (clientId) => `/clients/${clientId}/priority-vault`, // Save a card on file for future use
  },

  // ── Passengers linked to a client profile ─────────────────────────────────
  CLIENT_PASSENGERS: {
    BY_ID: (passengerId) => `/client-passengers/${passengerId}`,
  },

  // ── Passengers linked to a specific quote ─────────────────────────────────
  QUOTE_PASSENGERS: {
    BY_ID: (passengerId) => `/quote-passengers/${passengerId}`,
  },

  // ── Checkout (card payment flow) ──────────────────────────────────────────
  // Endpoints used during the checkout process when a client pays by card.
  CHECKOUT: {
    CHARGE: '/checkout/charge/simulated',       // Test-mode charge
    CAPTURE: '/checkout/capture/simulated',     // Test-mode capture
    TOKEN: '/checkout/token',                   // Issue a one-time public token for a checkout session
    PAYMENTINTENTS: '/checkout/payment-intents',
    SESSION: '/passport/checkout/session',      // Create a Priority Commerce checkout session
  },

  // ── Mercury (invoicing partner) ───────────────────────────────────────────
  // Used to generate and send invoices through the Mercury platform.
  MERCURY: {
    INVOICE: '/mercury/customers/invoice',
  },

  // ── MX Merchant (card processing) ────────────────────────────────────────
  // Handles card payments and payment links through MX Merchant.
  MXMERCHANT: {
    PAYMENT_LINKS: '/mxmerchant/payment-links',
    CAPTURE_PAYMENT: '/mxmerchant/transactions/payments/capture',    // Finalise and collect a card payment
    PRE_AUTHORIZE: '/mxmerchant/transactions/payments/pre-authorize', // Reserve funds on a card before charging
    SALE_COMPLETION: '/mxmerchant/transactions/payments/sale-completion',
  },

  // ── Priority Commerce / Passport (card vaulting) ─────────────────────────
  // Used to securely save and charge cards via Priority Commerce.
  PASSPORT: {
    CAPTURE_PAYMENT: '/passport/checkout/payment/capture',
  },

  // ── Airports ──────────────────────────────────────────────────────────────
  AIRPORTS: {
    BASE: '/airports',
  },

  // ── Aviapages (Fixed-Base Operators / FBOs) ───────────────────────────────
  // FBOs are the ground-handling facilities at airports (fuel, lounges, etc.).
  AVIAPAGES: {
    FBO: '/aviapages/fbo',
  },

  // ── AI / LLM features ────────────────────────────────────────────────────
  // AI-powered features such as estimating flight arrival times.
  LLM: {
    ARRIVAL_TIME: '/llm/arrival-time',
  },

  // ── File uploads ──────────────────────────────────────────────────────────
  UPLOADS: {
    BASE: '/upload',
  },

  // ── Flights (live trip tracking) ──────────────────────────────────────────
  // Represents individual flight legs within a trip, used for tracking.
  FLIGHTS: {
    BASE: '/flights',
    BY_ID: (flightId) => `/flights/${flightId}`,
    BATCH: '/flights/batch', // Update multiple flights at once
  },

  // ── JetNet (aircraft registry data) ──────────────────────────────────────
  // Third-party source for aircraft ownership and registration information.
  JETNET: {
    AIRCRAFTS: '/jetnet/aircrafts',
  },

  // ── FlightRadar24 (live flight tracking) ─────────────────────────────────
  // Real-time flight position data and airport information.
  FLIGHTRADAR24: {
    LIVE_POSITIONS: '/flightradar24/live/flight-positions',
    FLIGHT_SUMMARY: '/flightradar24/flight-summary',
    AIRPORT: (code) => `/flightradar24/airports/${code}/full`, // e.g. code = "EGLL" for Heathrow
    FLIGHT_TRACKS: '/flightradar24/flight-tracks',
    TOKEN: '/flightradar24/tracking-token',                    // Short-lived token to authorise tracking requests
  },

  // ── Stripe Connect (broker payouts) ──────────────────────────────────────
  // Stripe is used to pay out brokers and manage their connected accounts.
  STRIPE: {
    CONNECTED_ACCOUNT: (orgId) => `/organizations/${orgId}/stripe-account`,
    ONBOARDING_START: (orgId) => `/organizations/${orgId}/stripe-onboarding/start`,
    PAYMENT_LINKS: '/stripe/payment-links',
  },

  // ── Invoices ──────────────────────────────────────────────────────────────
  INVOICES: {
    BASE: '/invoices',
    BY_ID: (invoiceId) => `/invoices/${invoiceId}`,
  },

  // ── Payments ──────────────────────────────────────────────────────────────
  PAYMENTS: {
    BASE: '/payments',
  },

  // ── Financial Reports ─────────────────────────────────────────────────────
  // Server-driven, unified quote+invoice ledger. Feeds the /financials/reports
  // page. Paged list + separate KPI summary — both share the same filter
  // contract so cards and table always stay in lockstep.
  FINANCIAL_REPORTS: {
    BASE: '/financial/reports',
    SUMMARY: '/financial/reports/summary',
  },
  
  // ── AWS (file storage) ───────────────────────────────────────────────────
  // Generates secure, time-limited URLs for uploading files to cloud storage.
  AWS: {
    S3_SIGNED_URL: '/aws/s3/signed-url',
    // TODO(BE): endpoint to delete an S3 object by key isn't live yet.
    // Path shape is a placeholder — real path will be whatever BE ships.
    // Callsites that would fire this are kept in the source, commented,
    // so uncommenting is a one-line change once the endpoint lands.
    S3_DELETE_OBJECT: '/aws/s3/object',
  },

};

/**
 * Appends query parameters to a URL path.
 * e.g. buildUrlWithParams('/clients/client-secrets', { token: 'abc' })
 *      → '/clients/client-secrets?token=abc'
 */
export function buildUrlWithParams(baseUrl, params = {}) {
  const url = new URL(baseUrl, window.location.origin);

  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      url.searchParams.append(key, value);
    }
  });

  return url.pathname + url.search;
}
